import { HttpClient, HttpHeaders } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import { CSVInterface, User, UserPgc, UserResponse, UsersResponseMap } from '@interfaces'
import { BehaviorSubject, Observable, map } from 'rxjs'
import { buildConfigQueryParams } from './config-query-params.helper'
import { normalizeRemoteSearchStringParam } from '../remote-text-search.helper'

export interface UsersFilters {
	searchString?: string
	name?: string
	surname?: string
	mobile?: string
	email?: string
	active?: boolean
}

@Injectable({
	providedIn: 'root'
})
export class UsersService implements CSVInterface {
	private http = inject(HttpClient)
	url = '/users'
	private readonly textFilterKeys: (keyof UsersFilters)[] = ['searchString', 'name', 'surname', 'mobile', 'email']

	private _creatingUser: boolean = false
	private creatingUserSubject = new BehaviorSubject<boolean>(false)
	creatingUser$ = this.creatingUserSubject.asObservable()

	// Trae una lista paginada de los usuarios
	getUsers(page = 1, size = 15, filters: UsersFilters = { active: true }): Observable<UsersResponseMap> {
		const normalizedFilters = this.normalizeUsersFilters(filters)
		const params = buildConfigQueryParams({
			page,
			size,
			...normalizedFilters
		})

		return this.http.get<UserResponse>(this.url, { params }).pipe(
			map(resp => {
				return {
					...resp,
					content: resp.content.map((u: UserPgc) => ({
						id: u.id,
						user: {
							id: u.id,
							avatar: u.avatar,
							name: u.name,
							surname: u.surname,
							sysuser: u.sysUser?.sysUser,
							mobile: u.mobile,
							pgcProfile: u.pgcProfile,
							verified: u.sysUser?.validated,
							countryMobile: u.countryMobile
						}
					}))
				} as UsersResponseMap
			})
		)
	}

	// Crea un usuario si no existe, si no, lo edita
	createUser(data: UserPgc): Observable<UserPgc> {
		return this.http.post<UserPgc>(`${this.url}`, data)
	}

	updateUser(data: UserPgc): Observable<UserPgc> {
		return this.http.put<UserPgc>(`${this.url}`, data)
	}

	// Marca una serie de usuarios como inactivos
	enableDisableUsers(userIds: number[], active: boolean): Observable<boolean> {
		return this.http.put<boolean>(`${this.url}/status?active=${active}`, userIds)
	}

	// Elimina por completo un usuario
	delete(userList: number[]): Observable<void> {
		return this.http.delete<void>(`${this.url}`, { body: userList })
	}

	// Marca el estado global de creación de usuarios como false
	resetCreatingState() {
		this._creatingUser = false
		this.creatingUserSubject.next(this._creatingUser)
	}

	// Trae un blob con un (CSV) de tipo texto como ejemplo para la importación
	templateCSV() {
		const headers = new HttpHeaders({
			'Content-Type': 'text/csv',
			responseType: 'blob',
			Accept: 'text/csv',
			observe: 'response'
		})
		return this.http.get(`${this.url}/importTemplate`, { headers, responseType: 'text' })
	}

	// Alterna el estado global de creación de usuarios
	toogleCreatingUser() {
		this._creatingUser = !this._creatingUser
		this.creatingUserSubject.next(this._creatingUser)
	}

	// Sube un archivo de texto (CSV) para importar usuarios en masa
	uploadCSV(file: File) {
		const formData = new FormData()
		formData.append('file', file, file.name)

		return this.http.post<User[]>(`${this.url}/import`, formData, { observe: 'response' })
	}

	private normalizeUsersFilters(filters: UsersFilters): UsersFilters {
		const normalizedFilters: UsersFilters = { ...filters }
		const mutableFilters = normalizedFilters as Record<string, unknown>

		for (const key of this.textFilterKeys) {
			const value = filters[key]
			if (typeof value !== 'string') continue

			const normalizedValue = normalizeRemoteSearchStringParam(value)
			if (normalizedValue) {
				mutableFilters[key] = normalizedValue
			} else {
				delete mutableFilters[key]
			}
		}

		return normalizedFilters
	}
}
